import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { ScrollToTop } from "./components/ScrollToTop";
import { useUser } from "./contexts/UserContext";

import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import Terms from "./pages/Terms";
import Dashboard from "./pages/Dashboard";
import CustomerDashboard from "./pages/CustomerDashboard";
import CustomerProfile from "./pages/CustomerProfile";
import AdminProfile from "./pages/AdminProfile";
import Customers from "./pages/Customers";
import Warehouses from "./pages/Warehouses";
import Shipments from "./pages/Shipments";
import Trips from "./pages/Trips";
import Invoices from "./pages/Invoices";
import Reports from "./pages/Reports";
import Users from "./pages/Users";
import ScanPage from "./components/qr/ScanPage";
import SourcingRequests from "./pages/SourcingRequests";
import Wallet from "./pages/Wallet";
import WalletAdmin from "./pages/WalletAdmin";
import NotFound from "./pages/NotFound";

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user } = useUser();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Dashboard routing component - shows different dashboard based on role
function DashboardRouter() {
  const { user } = useUser();

  if (user?.role === 'customer') {
    return <CustomerDashboard />;
  }

  return <Dashboard />;
}

// Profile routing component - shows different profile based on role
function ProfileRouter() {
  const { user } = useUser();

  if (user?.role === 'admin') {
    return <AdminProfile />;
  }

  // For customer and warehouse users, use CustomerProfile
  return <CustomerProfile />;
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/" element={<Home />} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardRouter /></ProtectedRoute>} />
        <Route path="/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
        <Route path="/warehouses" element={<ProtectedRoute><Warehouses /></ProtectedRoute>} />
        <Route path="/sourcing-requests" element={<ProtectedRoute><SourcingRequests /></ProtectedRoute>} />
        <Route path="/sourcing-requests/all" element={<ProtectedRoute><SourcingRequests filter="all" /></ProtectedRoute>} />
        <Route path="/sourcing-requests/pending" element={<ProtectedRoute><SourcingRequests filter="pending" /></ProtectedRoute>} />
        <Route path="/sourcing-requests/quoted" element={<ProtectedRoute><SourcingRequests filter="quoted" /></ProtectedRoute>} />
        <Route path="/sourcing-requests/approved" element={<ProtectedRoute><SourcingRequests filter="approved" /></ProtectedRoute>} />
        <Route path="/sourcing-requests/completed" element={<ProtectedRoute><SourcingRequests filter="completed" /></ProtectedRoute>} />
        <Route path="/shipments" element={<ProtectedRoute><Shipments /></ProtectedRoute>} />
        <Route path="/shipments/all" element={<ProtectedRoute><Shipments filter="all" /></ProtectedRoute>} />
        <Route path="/shipments/on-the-way" element={<ProtectedRoute><Shipments filter="on-the-way" /></ProtectedRoute>} />
        <Route path="/shipments/processing" element={<ProtectedRoute><Shipments filter="processing" /></ProtectedRoute>} />
        <Route path="/shipments/in-transit" element={<ProtectedRoute><Shipments filter="in-transit" /></ProtectedRoute>} />
        <Route path="/shipments/at-destination" element={<ProtectedRoute><Shipments filter="at-destination" /></ProtectedRoute>} />
        <Route path="/shipments/delivered" element={<ProtectedRoute><Shipments filter="delivered" /></ProtectedRoute>} />
        <Route path="/trips" element={<ProtectedRoute><Trips /></ProtectedRoute>} />
        <Route path="/trips/all" element={<ProtectedRoute><Trips filter="all" /></ProtectedRoute>} />
        <Route path="/trips/planning" element={<ProtectedRoute><Trips filter="planning" /></ProtectedRoute>} />
        <Route path="/trips/in-transit" element={<ProtectedRoute><Trips filter="in-transit" /></ProtectedRoute>} />
        <Route path="/trips/at-destination" element={<ProtectedRoute><Trips filter="at-destination" /></ProtectedRoute>} />
        <Route path="/trips/completed" element={<ProtectedRoute><Trips filter="completed" /></ProtectedRoute>} />
        <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
        <Route path="/invoices/all" element={<ProtectedRoute><Invoices filter="all" /></ProtectedRoute>} />
        <Route path="/invoices/draft" element={<ProtectedRoute><Invoices filter="draft" /></ProtectedRoute>} />
        <Route path="/invoices/sent" element={<ProtectedRoute><Invoices filter="sent" /></ProtectedRoute>} />
        <Route path="/invoices/paid" element={<ProtectedRoute><Invoices filter="paid" /></ProtectedRoute>} />
        <Route path="/invoices/overdue" element={<ProtectedRoute><Invoices filter="overdue" /></ProtectedRoute>} />
        <Route path="/invoices/partial" element={<ProtectedRoute><Invoices filter="partial" /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
        <Route path="/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfileRouter /></ProtectedRoute>} />
        <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
        <Route path="/wallet-admin" element={<ProtectedRoute><WalletAdmin /></ProtectedRoute>} />
        {/* QR Scanner Route - Easy to remove */}
        <Route path="/scan" element={<ProtectedRoute><ScanPage /></ProtectedRoute>} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}
export default AppRouter;